/**
 * Billing record Audit Log — seed rows + central Change History projection.
 * Columns: Date and Time · Event · Details · Related Record
 */

import {
  getLocalAuditLogRows,
  syncLocalAuditLogsToChangeHistory,
} from '../../../data/changeHistoryAudit';

const SEED_AUDIT_LOGS_BY_BUSINESS = {
  'BUS-9934': [
    {
      id: 'BIL-AUD-9934-1',
      dateTime: '2026-08-07T06:00:00+10:00',
      event: 'Payment succeeded',
      details: 'System · Monthly Pro renewal · AUD 19.99',
      relatedRecord: 'txn-werribee-2026-08',
    },
    {
      id: 'BIL-AUD-9934-2',
      dateTime: '2026-07-07T06:00:00+10:00',
      event: 'Payment succeeded',
      details: 'System · Monthly Pro renewal · AUD 19.99',
      relatedRecord: 'txn-werribee-2026-07',
    },
    {
      id: 'BIL-AUD-9934-3',
      dateTime: '2024-03-20T10:15:00+10:00',
      event: 'Subscription started',
      details: 'System · Plan set to Pro',
      relatedRecord: 'sub-werribee',
    },
  ],
  'BUS-7142': [
    {
      id: 'BIL-AUD-7142-1',
      dateTime: '2026-07-20T09:30:00+10:00',
      event: 'Trial started',
      details: 'System · Basic trial · ends 17 Aug 2026',
      relatedRecord: 'sub-apex',
    },
  ],
  'BUS-3625': [
    {
      id: 'BIL-AUD-3625-1',
      dateTime: '2026-08-26T14:22:00+10:00',
      event: 'Payment failed',
      details: 'Stripe · Card declined · Grace Period started',
      relatedRecord: 'txn-urban-2026-08',
    },
    {
      id: 'BIL-AUD-3625-2',
      dateTime: '2026-07-26T09:00:00+10:00',
      event: 'Payment succeeded',
      details: 'System · Monthly Basic renewal · AUD 9.99',
      relatedRecord: 'txn-urban-2026-07',
    },
  ],
  'BUS-5810': [
    {
      id: 'BIL-AUD-5810-1',
      dateTime: '2026-08-10T11:05:00+10:00',
      event: 'Cancellation requested',
      details: 'Business · Effective at end of period 1 Sep 2026',
      relatedRecord: 'sub-northside',
    },
    {
      id: 'BIL-AUD-5810-2',
      dateTime: '2026-08-01T09:15:00+10:00',
      event: 'Payment succeeded',
      details: 'System · Monthly Pro renewal · AUD 19.99',
      relatedRecord: 'txn-northside-2026-08',
    },
  ],
  'BUS-4478': [
    {
      id: 'BIL-AUD-4478-1',
      dateTime: '2026-07-28T11:40:00+10:00',
      event: 'Payment failed',
      details: 'Stripe · Card declined · Subscription set Inactive',
      relatedRecord: 'txn-rapid-2026-07',
    },
    {
      id: 'BIL-AUD-4478-2',
      dateTime: '2026-07-28T11:41:00+10:00',
      event: 'Subscription inactivated',
      details: 'System · Payment not recovered',
      relatedRecord: 'sub-rapid',
    },
  ],
};

export function formatBillingAuditDateTime(value) {
  if (!value) return '—';
  if (typeof value === 'string' && !value.includes('T') && value.includes(',')) {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return (
    date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' +
    date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
  );
}

/**
 * Returns Audit Log rows for a billing business record.
 * Seeds sync into central Change History once, then all rows (including
 * newer recordBillingActivity entries) are read from the store.
 */
export function getBillingAuditLogsForBusiness(businessId) {
  const id = String(businessId || '').trim();
  if (!id) return [];

  const seed = SEED_AUDIT_LOGS_BY_BUSINESS[id] || [];
  syncLocalAuditLogsToChangeHistory({
    logs: seed,
    module: 'Billing & Subscriptions',
    sourceModuleRef: 'billing_subscriptions',
    sourceItemRef: id,
    accountRef: id,
    changedItem: id,
    itemId: id,
  });

  const fromStore = getLocalAuditLogRows({
    sourceModuleRef: 'billing_subscriptions',
    sourceItemRef: id,
  });

  return [...fromStore].sort((a, b) => {
    const first = a.timestamp || a.dateTime || '';
    const second = b.timestamp || b.dateTime || '';
    return String(second).localeCompare(String(first));
  });
}
